import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { Section } from '@/features/app-shell/section'
import {
	getNetworkLogsEnabled,
	isNetworkLogsPath,
} from '@/features/integrations/network-logs-allowlist'

import { ErrorBoundary } from '@/components/common/error-boundary'

export const Route = createFileRoute('/_dashboard/integrations')({
	/**
	 * Network-logs gate: a tenant outside the prod allowlist gets the pre-1.32
	 * surface, so the Overview and collector routes land on the Cloud list.
	 *
	 * Lives on the layout so it covers every child at once, and because a
	 * parent `beforeLoad` that throws stops the children from loading, the
	 * Overview and collector loaders (and their prefetches) never fire for a
	 * gated tenant.
	 *
	 * Tenant switch needs no `loaderDeps` here: unlike a loader, `beforeLoad`
	 * runs on every navigation, and `_dashboard`'s runs first and seeds the new
	 * tenant, so this reads the target tenant. A switch from
	 * `/integrations/collectors/<id>` resolves to `/integrations/collectors`
	 * (`resolvePostSwitchPath`), which this then catches. (`_dashboard.tsx`'s
	 * `loaderDeps` comment says a matched parent's `beforeLoad` does not re-run
	 * on a `?t=`-only change; this route's test pins the opposite for the
	 * router version in use — navigating within the layout, and swapping only
	 * `?t=`, both re-run this gate.)
	 *
	 * No `search`: `_dashboard`'s `retainSearchParams(['t'])` carries `?t=`
	 * onto the target, as with the other child-route redirects. `replace` keeps
	 * the gated URL out of history, so Back does not bounce through it again.
	 */
	beforeLoad: ({ location }) => {
		if (!getNetworkLogsEnabled() && isNetworkLogsPath(location.pathname)) {
			throw redirect({ to: '/integrations/cloud', replace: true })
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Integrations | BitLyft AIR®',
			},
		],
	}),

	component: IntegrationsRoute,
	pendingComponent: IntegrationsSkeleton,
	errorComponent: RouteErrorComponent,
})

function IntegrationsRoute() {
	return (
		<ErrorBoundary feature="integrations">
			<Section>
				<Outlet />
			</Section>
		</ErrorBoundary>
	)
}

function IntegrationsSkeleton() {
	return (
		<Section>
			<Outlet />
		</Section>
	)
}
